import { ref } from 'vue'
import { baseApi } from '~/api/base'

/**
 * @description 文件上传组合函数
 * @description 提供文件上传到OSS的通用方法
 * @returns {Object} 上传方法和状态
 */
export const useFileUpload = () => {
  const uploading = ref(false)
  const uploadProgress = ref(0)

  /**
   * @description 上传文件到OSS
   * @param {File} file - 要上传的文件
   * @param {Object} options - 上传选项
   * @param {boolean} options.isImFile - 是否是IM文件，默认false
   * @param {number} options.attachFileGroupId - 附件分组ID，默认0
   * @param {Function} options.onProgress - 上传进度回调函数
   * @param {boolean} options.returnUploadResult - 是否返回包含临时预览地址的上传结果，默认false
   * @returns {Promise<string|{filePath: string, previewUrl: string}>} 默认返回OSS文件路径；按需返回上传结果
   * @example
   * const { uploadFile } = useFileUpload()
   * const filePath = await uploadFile(file)
   * console.log('文件路径:', filePath)
   */
  const uploadFile = async (file, options = {}) => {
    const {
      isImFile = false,
      attachFileGroupId = 0,
      onProgress,
      returnUploadResult = false,
    } = options

    if (!file) {
      throw new Error('文件不能为空')
    }

    uploading.value = true
    uploadProgress.value = 0

    try {
      // 第一步：获取OSS预签名URL
      const preSignResult = await baseApi.getPreSignUrl(file.name, file.size, isImFile)
      // 响应拦截器可能返回 data 字段，需要处理不同的响应格式
      const resultData = preSignResult?.data || preSignResult
      if (!resultData || !resultData.preSignUrl) {
        throw new Error('获取上传地址失败')
      }

      const {
        preSignUrl,
        previewUrl: prePreviewUrl = '',
        requiredHeaders = {},
        fileId,
        filePath: preFilePath,
      } = resultData

      // 第二步：上传文件到OSS
      if (onProgress) {
        uploadProgress.value = 50
        onProgress(50)
      }

      await baseApi.uploadFileToOss(preSignUrl, file, requiredHeaders)

      if (onProgress) {
        uploadProgress.value = 80
        onProgress(80)
      }

      // 第三步：通知服务器上传成功，获取filePath
      // 注意：如果 getPreSignUrl 已经返回了 filePath，可以直接使用
      let finalFilePath = preFilePath
      let finalPreviewUrl = prePreviewUrl

      if (!finalFilePath) {
        const uploadSuccessResult = await baseApi.uploadSuccess(
          fileId,
          file.size,
          attachFileGroupId
        )
        // 响应拦截器可能返回 data 字段
        const successData = uploadSuccessResult?.data || uploadSuccessResult
        if (!successData || !successData.filePath) {
          throw new Error('获取文件路径失败')
        }
        finalFilePath = successData.filePath
        finalPreviewUrl = successData.previewUrl || finalPreviewUrl
      }

      if (onProgress) {
        uploadProgress.value = 100
        onProgress(100)
      }

      return returnUploadResult
        ? { filePath: finalFilePath, previewUrl: finalPreviewUrl }
        : finalFilePath
    } catch (error) {
      console.error('文件上传失败:', error)
      throw error
    } finally {
      uploading.value = false
      uploadProgress.value = 0
    }
  }

  /**
   * @description 上传图片文件（带验证）
   * @param {File} file - 图片文件
   * @param {Object} options - 上传选项
   * @param {Array<string>} options.allowedTypes - 允许的文件类型，默认 ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
   * @param {number} options.maxSize - 最大文件大小（字节），默认 5MB
   * @param {boolean} options.isImFile - 是否是IM文件，默认false
   * @param {number} options.attachFileGroupId - 附件分组ID，默认0
   * @param {Function} options.onProgress - 上传进度回调函数
   * @param {boolean} options.returnUploadResult - 是否返回包含临时预览地址的上传结果，默认false
   * @returns {Promise<string|{filePath: string, previewUrl: string}>} 默认返回OSS文件路径；按需返回上传结果
   * @example
   * import { BaseMessage } from '~/utils/baseMessage'
   *
   * const { uploadImage } = useFileUpload()
   * try {
   *   const filePath = await uploadImage(file, { maxSize: 2 * 1024 * 1024 })
   *   console.log('图片路径:', filePath)
   * } catch (error) {
   *   BaseMessage.error(error.message)
   * }
   */
  const uploadImage = async (file, options = {}) => {
    const {
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      maxSize = 5 * 1024 * 1024, // 默认5MB
      isImFile = false,
      attachFileGroupId = 0,
      onProgress,
      returnUploadResult = false,
    } = options

    // 验证文件类型
    if (!allowedTypes.includes(file.type)) {
      const allowedExtensions = allowedTypes
        .map((type) => type.split('/')[1].toUpperCase())
        .join('、')
      throw new Error(`请选择图片文件（${allowedExtensions}）`)
    }

    // 验证文件大小
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / 1024 / 1024).toFixed(0)
      throw new Error(`图片大小不能超过 ${maxSizeMB}MB`)
    }

    return uploadFile(file, {
      isImFile,
      attachFileGroupId,
      onProgress,
      returnUploadResult,
    })
  }

  /**
   * @description 上传多个文件
   * @param {Array<File>} files - 文件数组
   * @param {Object} options - 上传选项
   * @returns {Promise<Array<string>>} 返回文件路径数组
   * @example
   * const { uploadMultipleFiles } = useFileUpload()
   * const filePaths = await uploadMultipleFiles([file1, file2])
   */
  const uploadMultipleFiles = async (files, options = {}) => {
    if (!Array.isArray(files) || files.length === 0) {
      throw new Error('文件列表不能为空')
    }

    const uploadPromises = files.map((file) => uploadFile(file, options))
    return Promise.all(uploadPromises)
  }

  return {
    uploading,
    uploadProgress,
    uploadFile,
    uploadImage,
    uploadMultipleFiles,
  }
}
